import {Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy} from '@angular/core';
import {distinctUntilChanged, debounceTime} from 'rxjs/operators';
import {detailsExpand} from '../../../../../../common';
import {BaseEmailClassDirective} from 'src/app/modules/common/components/email-table/base-email-class.directive';
import {EmailService} from 'src/app/modules/pbm/services/email.service';
import {SharedService} from 'src/app/modules/common/services/shared-service';
import {FormControl} from '@angular/forms';
import {CreateNameValueDTO, NameValueDTO} from 'src/app/modules/common/models/NameValueDTO';
import {ApplicationPersonDTO} from 'src/app/modules/onboarding-admin-portal/models/application/ApplicationPersonDTO';
import {ApplicationDTO} from 'src/app/modules/onboarding-admin-portal/models/application/ApplicationDTO';
import {FileUploadService} from 'src/app/modules/common/services/file-upload.service';
import {TableControl} from 'src/app/modules/common/models/TableControl';
import {PagedRequestDTO} from 'src/app/modules/common/models/PagedRequestDTO';
import {PbmRequestBuilder} from 'src/app/modules/pbm/services/pbm-request-builder';
import {PagedFilterDTO} from 'src/app/modules/common/models/PagedFilterDTO';
import {takeUntil, Subject, Subscription} from 'rxjs';
import {PagedResponseDTO} from 'src/app/modules/common/models/PagedResponseDTO';
import {EmailDTO} from 'src/app/modules/pbm/models/email/EmailDTO';
import {CommunicationTriggerType} from 'src/app/modules/common/types/communication-trigger-type.enum';
import {TableColumns, IconName} from 'src/app/modules/common/components/email-table/email-table.component';
import {Common} from '../../../../../../common/util/Util';

@Component({
  selector: 'greydom-application-emails',
  templateUrl: './application-emails.component.html',
  styleUrls: [
    '../../../../../../../../styles/global/_management-styles.scss',
    '../../../../../../../../styles/global/_dialog-styles.scss',
    './application-emails.component.scss'
  ],
  animations: [detailsExpand],
})
export class ApplicationEmailsComponent extends BaseEmailClassDirective implements OnInit, OnChanges, OnDestroy {
  readonly debounceTime = Common.DEBOUNCE_TIME;

  @Input() application: ApplicationDTO;

  persons: ApplicationPersonDTO[] = [];
  personsNameValueDTO: NameValueDTO[] = [];
  defaultPersonSelected: string;
  inputSubscription: Subscription;
  textChanged$ = new Subject<string>();
  selectedApplicationIdControl = new FormControl();
  subjectLineSearchControl = new FormControl();

  protected constructor(
    protected emailService: EmailService,
    protected sharedService: SharedService,
    protected fileUploadService: FileUploadService) {
    super(emailService, sharedService, fileUploadService);
  }

  ngOnInit(): void {
    this.initSubjectSearchSubscription();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.application?.currentValue) {
      this.getPersonAndDependentsPersons();
    }
  }

  private initSubjectSearchSubscription() {
    this.inputSubscription = this.subjectLineSearchControl.valueChanges.subscribe((value) => this.textChanged$.next(value));
    this.textChanged$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      debounceTime(this.debounceTime)
    ).subscribe({
      next: () => this.getListOfEmails(this.emailTableControl),
      error: error => console.log(error)
    });
  }

  getPersonAndDependentsPersons(): void {
    this.persons = this.application.membership?.persons || [];
    this.personsNameValueDTO = this.persons
      .sort((a: ApplicationPersonDTO, b: ApplicationPersonDTO) => {

        // Note: In case dependentNumber is null use applicationPersonId

        if (a.dependentNumber && b.dependentNumber) {
          return a.dependentNumber.localeCompare(b.dependentNumber);
        }
        else {
          return a.applicationPersonId - b.applicationPersonId;
        }
      })
      .map(person =>
        CreateNameValueDTO(person.applicationPersonId, this.getDisplayString(person))
      );

    if (this.personsNameValueDTO.length > 0) {
      this.defaultPersonSelected = this.personsNameValueDTO[0]?.name;
      this.selectedApplicationIdControl.patchValue(this.personsNameValueDTO[0]?.value);
      this.getListOfEmails(this.emailTableControl);
    }
  }

  getDisplayString(person: ApplicationPersonDTO): string {
    if (!person) {
      return;
    }
    return `${person.firstName} ${person.lastName}`;
  }

  onSelectionChange(selectedPerson: NameValueDTO) {
    if (selectedPerson) {
      this.selectedApplicationIdControl.setValue(selectedPerson.value);
      this.getListOfEmails(this.emailTableControl);
    }
  }

  getToAddress(row: EmailDTO): string {
    return row.editedToAddress || row.toAddress;
  }

  showIcon(row: EmailDTO, iconName: IconName): boolean {
    switch (iconName) {
    case IconName.Send:
      return (
        !row[TableColumns.IsSent] &&
        !row.sendEmailChecked &&
        !row.resetEmailConfirmation
      );
    case IconName.Reset:
      return (
        row[TableColumns.EmailId] &&
        !(row[TableColumns.CommunicationTriggerType]?.name === CommunicationTriggerType.ManualTriggerType) &&
        !row.sendEmailChecked &&
        !row.resetEmailConfirmation
      );
    default:
      return false;
    }
  }

  protected getListOfEmails(tableControl: TableControl): void {
    const pagedRequestDTO: PagedRequestDTO = PbmRequestBuilder.getListOfEmailPagedRequestDTO(tableControl);
    pagedRequestDTO.fieldMap = {};

    const personPagedFilterDTO: PagedFilterDTO = new PagedFilterDTO();
    personPagedFilterDTO.name = 'applicationPersonByApplicationPersonId.applicationPersonId';
    personPagedFilterDTO.value = this.selectedApplicationIdControl?.value;
    pagedRequestDTO.fieldMap[`applicationPersonByApplicationPersonId`] = personPagedFilterDTO;

    if (this.subjectLineSearchControl?.value && this.subjectLineSearchControl?.value.length > 0) {
      const subjectLineIdentifier = 'subject';
      const subjectLineSearchFilterDTO: PagedFilterDTO = new PagedFilterDTO();
      subjectLineSearchFilterDTO.name = subjectLineIdentifier;
      subjectLineSearchFilterDTO.value = this.subjectLineSearchControl?.value;
      pagedRequestDTO.fieldMap[subjectLineIdentifier] = subjectLineSearchFilterDTO;
    }

    this.emailService.getListOfEmails(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: PagedResponseDTO<EmailDTO>) => {
          response.resultList.forEach((item: EmailDTO): void => {
            item.sendEmailChecked = false;
            item.emailSentBusyCounter = 0;
          });
          this.emailDataSource.data = response.resultList;
          this.emailTableControl.length = response.totalRecords;
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error?.businessMessage, 'Dismiss');
        }
      });
  }

  ngOnDestroy(): void {
    this.inputSubscription.unsubscribe();
  }
}
